import React from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { useCart } from "./CartContext";
import { NavLink } from "react-router-dom";

const EventModal = ({
  event,
  course,
  spiltCourseName,
  registerSeats,
  setRegisterSeats,
  setStraper,
}) => {
  const { cart, dispatch } = useCart();

  // Calculate the available seats
  const availableSeats =
    event?.remainingSeats > event?.seatsTotal
      ? event.seatsTotal
      : event?.remainingSeats;

  // Generate an array of available seat numbers
  const seatOptions = Array.from({ length: availableSeats }, (_, i) => i + 1);

  const handleAddToCart = () => {
    if (registerSeats === 0) {
      toast.error("Please add seats!");
    } else {
      const eventCart = cart.filter((item) => item.id === event._id);
      if (eventCart.length > 0) {
        const cartItem = {
          id: eventCart[0].id,
          seats: Number(registerSeats),
        };

        dispatch({ type: "UPDATE_EVENT_SEATS_CART", payload: cartItem });
        toast.success("Class updated cart!");
        setStraper(2);
      } else {
        const cartItem = {
          id: event._id,
          name: `${spiltCourseName(course.name)} - ${moment
            .utc(event.startDate)
            .format("MMMM DD, YYYY")} - ${event.campus}`,
          price: course.price,
          seats: Number(registerSeats),
          event,
          course,
        };
        dispatch({ type: "ADD_TO_CART", payload: cartItem });
        toast.success("Class added to cart!");
        setStraper(2);
      }
    }
  };

  const handleSeatsChange = (e) => {
    const value = e.target.value;
    setRegisterSeats(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#183864] to-[#B02526] rounded-full mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#183864] mb-2">
            Select Your Class
          </h1>
          <p className="text-gray-600 text-lg">
            Choose the number of seats for your training session
          </p>
        </div>

        {/* Class Information Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-[#183864] to-[#B02526] p-6 text-white">
            <h2 className="text-xl md:text-2xl font-bold mb-2">
              Class Details
            </h2>
            <div className="flex flex-wrap items-center gap-2 text-red-100">
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                {spiltCourseName(course.name)}
              </span>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                {moment.utc(event.startDate).format("MMMM DD, YYYY")}
              </span>
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                {event.campus}
              </span>
            </div>
          </div>

          <div className="p-6">
            {/* Seat Selection Section */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-[#183864]/10 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-[#183864]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#183864]">
                      Number of Attendees
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Select how many people will attend
                    </p>
                  </div>
                </div>

                {event.eventType !== "redirect-events" && (
                  <div className="relative">
                    <select
                      className="w-full h-14 px-4 text-lg border-2 border-gray-300 rounded-xl focus:border-[#B02526] focus:ring-2 focus:ring-[#B02526]/20 bg-white transition-all duration-200 appearance-none cursor-pointer hover:border-[#B02526] shadow-sm"
                      value={registerSeats}
                      onChange={handleSeatsChange}
                      required
                      style={{
                        backgroundImage: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                      }}
                    >
                      <option value="" disabled className="text-gray-500">
                        Select Number of Seats
                      </option>
                      {seatOptions?.map((seat) => (
                        <option
                          key={seat}
                          value={seat}
                          className="text-[#183864] bg-white py-2"
                        >
                          {seat} {seat === 1 ? "Person" : "People"}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-[#B02526]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-[#B02526]/10 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-[#B02526]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#183864]">
                      Price Per Person
                    </h3>
                    <p className="text-2xl font-bold text-[#B02526]">
                      ${course.price} CAD
                    </p>
                    <p className="text-gray-500 text-sm">
                      *Price does not include taxes
                    </p>
                  </div>
                </div>

                {registerSeats && (
                  <div className="bg-[#183864]/5 rounded-xl p-4 border border-[#183864]/10">
                    <div className="flex justify-between items-center">
                      <span className="text-[#183864] font-medium">
                        Total ({registerSeats}{" "}
                        {registerSeats === 1 ? "person" : "people"})
                      </span>
                      <span className="text-xl font-bold text-[#B02526]">
                        ${(course.price * registerSeats).toFixed(2)} CAD
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Available Seats Info */}
            <div className="bg-[#183864]/5 rounded-xl p-4 mb-6 border border-[#183864]/10">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-[#183864] rounded-full flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-[#183864] font-semibold">
                    Available Seats: {availableSeats}
                  </p>
                  <p className="text-[#B02526] text-sm">
                    Secure your spot today!
                  </p>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-center">
              {event.eventType === "redirect-events" ? (
                <NavLink
                  to={event?.redirectEventLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative px-8 py-4 bg-gradient-to-r from-[#183864] to-[#B02526] hover:from-[#B02526] hover:to-[#183864] rounded-xl text-white font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-3"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  <span>Book External Class</span>
                </NavLink>
              ) : (
                <button
                  onClick={handleAddToCart}
                  className="group relative px-8 py-4 bg-gradient-to-r from-[#183864] to-[#B02526] hover:from-[#B02526] hover:to-[#183864] rounded-xl text-white font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-3"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0L12 21m0 0l2.5-3M12 21l2.5-3m2.5 0H16m1.5-3L19 16"
                    />
                  </svg>
                  <span>Add to Cart & Continue</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Class Details Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <h3 className="text-2xl font-bold text-[#183864] mb-6 flex items-center">
            <svg
              className="w-6 h-6 mr-3 text-[#B02526]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Additional Information
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 text-[#B02526] mt-1">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-[#183864]">Location</p>
                  <p className="text-gray-600">{event.campus || "--"}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 text-[#B02526] mt-1">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-[#183864]">Date</p>
                  <p className="text-gray-600">
                    {moment
                      .utc(event.startDate)
                      .diff(moment.utc(event.endDate), "days") !== 0
                      ? `${moment.utc(event.startDate).format("MMMM DD, YYYY")} - ${moment.utc(event.endDate).format("MMMM DD, YYYY")}`
                      : moment.utc(event.startDate).format("MMMM DD, YYYY")}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 text-[#B02526] mt-1">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-[#183864]">Time</p>
                  <p className="text-gray-600">{event?.timing || "--"}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 text-[#B02526] mt-1">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-[#183864]">Contact</p>
                  <p className="text-gray-600">{event.contact || "--"}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 text-[#B02526] mt-1">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-[#183864]">Address</p>
                  <p className="text-gray-600">{event.address || "--"}</p>
                </div>
              </div>

              {event.description && (
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 text-[#B02526] mt-1">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-[#183864]">Description</p>
                    <p className="text-gray-600">{event.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventModal;
